import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useVariant } from '../contexts/VariantContext';
import { useSEO } from '../contexts/SEOContext';
import { useDomainManager } from '../config';
import { renderCtaButton } from '../utils/ctaHelper';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { brand, domainConfig, cta } = useTheme();
  const { variant } = useVariant();
  const { generateProductStructuredData } = useSEO();
  const domainManager = useDomainManager(brand?.id);

  // Get product data from domain config
  const getProductData = () => {
    if (domainConfig?.products?.items) {
      return domainConfig.products.items.find(product => product.id === parseInt(id));
    }
    return null;
  };

  const product = getProductData();
  
  // Generate product structured data for SEO
  const productStructuredData = generateProductStructuredData(product);

  // Handle image path generation
  const getProductImagePath = (product) => {
    if (!product || !product.image) return '/assets/default-fallback.svg';
    
    const imageName = product.image;
    const domainId = brand?.id || 'default';
    
    // Try domain-specific image first
    const domainImagePath = `/assets/domains/${domainId}/${imageName}.png`;
    const domainImagePathJpg = `/assets/domains/${domainId}/${imageName}.jpg`;
    const domainImagePathGif = `/assets/domains/${domainId}/${imageName}.gif`;
    
    // Return the first available format
    return domainImagePath;
  };

  // Handle image error with fallback
  const handleImageErrorWithFallback = (e, domainId, imageName) => {
    const img = e.target;
    const currentSrc = img.src;
    
    // Try different formats
    if (currentSrc.includes('.png')) {
      img.src = `/assets/domains/${domainId}/${imageName}.jpg`;
    } else if (currentSrc.includes('.jpg')) {
      img.src = `/assets/domains/${domainId}/${imageName}.gif`;
    } else {
      img.src = '/assets/default-fallback.svg';
    }
  };

  // Get variant-specific styling
  const getVariantStyles = () => {
    switch (variant) {
      case 'minimal':
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          featuredBadge: 'bg-gray-800 text-white',
          detailCard: 'border-l-4 border-gray-400',
          borderDivider: 'border-gray-200',
          checkmark: 'text-green-500'
        };
      case 'modern':
        return {
          container: 'bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900',
          breadcrumb: 'bg-slate-800/50',
          textPrimary: 'text-white',
          textSecondary: 'text-purple-100',
          heading: 'text-white',
          buttonPrimary: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
          buttonSecondary: 'border border-purple-300 text-purple-100 hover:border-purple-400 hover:bg-purple-500/20',
          featuredBadge: 'bg-gradient-to-r from-purple-500 to-pink-500 text-white',
          detailCard: 'border-l-4 border-purple-400',
          borderDivider: 'border-purple-200/20',
          checkmark: 'text-purple-300'
        };
      case 'corporate':
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          featuredBadge: 'bg-blue-600 text-white',
          detailCard: 'border-l-4 border-blue-500',
          borderDivider: 'border-gray-200',
          checkmark: 'text-blue-500'
        };
      case 'creative':
        return {
          container: 'bg-gradient-to-br from-orange-100 via-pink-100 to-purple-100',
          breadcrumb: 'bg-white/80',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-700',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600',
          buttonSecondary: 'border border-orange-300 text-gray-700 hover:border-orange-400 hover:bg-orange-50',
          featuredBadge: 'bg-gradient-to-r from-orange-500 to-pink-500 text-white',
          detailCard: 'border-l-4 border-orange-400',
          borderDivider: 'border-orange-200',
          checkmark: 'text-orange-500'
        };
      case 'elegant':
        return {
          container: 'bg-slate-50',
          breadcrumb: 'bg-white',
          textPrimary: 'text-slate-900',
          textSecondary: 'text-slate-600',
          heading: 'text-slate-900',
          buttonPrimary: 'bg-slate-800 text-white hover:bg-slate-900',
          buttonSecondary: 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50',
          featuredBadge: 'bg-slate-800 text-white',
          detailCard: 'border-l-4 border-slate-400',
          borderDivider: 'border-slate-200',
          checkmark: 'text-slate-600'
        };
      case 'dark':
        return {
          container: 'bg-gray-900',
          breadcrumb: 'bg-gray-800',
          textPrimary: 'text-white',
          textSecondary: 'text-gray-300',
          heading: 'text-white',
          buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-800',
          featuredBadge: 'bg-blue-600 text-white',
          detailCard: 'border-l-4 border-blue-500',
          borderDivider: 'border-gray-700',
          checkmark: 'text-blue-400'
        };
      case 'clean':
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          featuredBadge: 'bg-gray-900 text-white',
          detailCard: 'border-l-4 border-gray-400',
          borderDivider: 'border-gray-200',
          checkmark: 'text-gray-500'
        };
      case 'grid':
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-green-600 text-white hover:bg-green-700',
          buttonSecondary: 'border border-green-200 text-green-600 hover:border-green-300 hover:bg-green-50',
          featuredBadge: 'bg-green-600 text-white',
          detailCard: 'border-l-4 border-green-500',
          borderDivider: 'border-gray-200',
          checkmark: 'text-green-500'
        };
      case 'magazine':
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-gray-900 text-white hover:bg-gray-800',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          featuredBadge: 'bg-gray-800 text-white',
          detailCard: 'border-l-4 border-gray-400',
          borderDivider: 'border-gray-200',
          checkmark: 'text-gray-600'
        };
      case 'professional':
        return {
          container: 'bg-slate-50',
          breadcrumb: 'bg-white',
          textPrimary: 'text-slate-900',
          textSecondary: 'text-slate-600',
          heading: 'text-slate-900',
          buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50',
          featuredBadge: 'bg-blue-600 text-white',
          detailCard: 'border-l-4 border-blue-500',
          borderDivider: 'border-slate-200',
          checkmark: 'text-blue-500'
        };
      case 'landing':
        return {
          container: 'bg-gradient-to-br from-blue-600 via-purple-600 to-blue-800',
          breadcrumb: 'bg-blue-600/50',
          textPrimary: 'text-white',
          textSecondary: 'text-white/90',
          heading: 'text-white',
          buttonPrimary: 'bg-white text-blue-700 hover:bg-blue-50 font-semibold shadow-lg',
          buttonSecondary: 'border-2 border-white text-white hover:bg-white hover:text-blue-700 font-semibold',
          featuredBadge: 'bg-white text-blue-700',
          detailCard: 'border-l-4 border-white',
          borderDivider: 'border-white/20',
          checkmark: 'text-white'
        };
      default:
        return {
          container: 'bg-white',
          breadcrumb: 'bg-gray-50',
          textPrimary: 'text-gray-900',
          textSecondary: 'text-gray-600',
          heading: 'text-gray-900',
          buttonPrimary: 'bg-blue-600 text-white hover:bg-blue-700',
          buttonSecondary: 'border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50',
          featuredBadge: 'bg-blue-600 text-white',
          detailCard: 'border-l-4 border-blue-500',
          borderDivider: 'border-gray-200',
          checkmark: 'text-blue-500'
        };
    }
  };

  const styles = getVariantStyles();

  // If product not found, redirect to products page
  if (!product) {
    navigate('/products');
    return null;
  }

  return (
    <div className={`min-h-screen ${styles.container}`}>
      {/* Product Structured Data for SEO */}
      {productStructuredData && (
        <Helmet>
          <title>{product.title} | {domainConfig?.name}</title>
          <meta name="description" content={product.description} />
          <script type="application/ld+json">
            {JSON.stringify(productStructuredData)}
          </script>
        </Helmet>
      )}

      {/* Breadcrumb */}
      <div className={`${styles.breadcrumb} py-4`}>
        <div className="max-w-7xl mx-auto px-6">
          <nav className="flex items-center space-x-2 text-sm">
            <button 
              onClick={() => navigate('/')}
              className={`hover:underline ${styles.textSecondary}`}
            >
              Home
            </button>
            <span className={styles.textSecondary}>/</span>
            <button 
              onClick={() => navigate('/products')}
              className={`hover:underline ${styles.textSecondary}`}
            >
              Products
            </button>
            <span className={styles.textSecondary}>/</span>
            <span className={styles.textPrimary}>{product.title}</span>
          </nav>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Product Image - Centered at Top */}
        <div className="flex justify-center mb-12">
          <div className="relative max-w-2xl">
            <img
              src={getProductImagePath(product)}
              alt={product.title}
              className="w-full h-96 object-cover rounded-xl shadow-lg"
              onError={(e) => handleImageErrorWithFallback(e, brand.id, product.image)}
            />
            {product.featured && (
              <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${styles.featuredBadge}`}>
                Featured
              </div>
            )}
          </div>
        </div>

        {/* Product Title and Description */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${styles.heading}`}>
            {product.title}
          </h1>
          <p className={`text-lg leading-relaxed max-w-3xl mx-auto ${styles.textSecondary}`}>
            {product.description}
          </p>
        </div>

        {/* Product Details Section - 2 Columns */}
        {product.details && product.details.length > 0 && (
          <div className="space-y-8">
            <h2 className={`text-2xl font-semibold text-center ${styles.heading}`}>
              Product Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {product.details.map((detail, index) => (
                <div key={index} className={`border-l-4 ${styles.detailCard} pl-4`}>
                  <h3 className={`font-semibold text-lg ${styles.textPrimary}`}>
                    {detail.title}
                  </h3>
                  {detail.description && (
                    <p className={`mt-2 ${styles.textSecondary}`}>
                      {detail.description}
                    </p>
                  )}
                  {detail.features && detail.features.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {detail.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <span className={`${styles.checkmark} mr-2`}>✓</span>
                          <span className={styles.textSecondary}>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA Button - Centered */}
        <div className={`text-center pt-12 border-t ${styles.borderDivider}`}>
          {renderCtaButton(cta?.secondary, 'Buy Now', `inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg transition-colors duration-200 ${styles.buttonPrimary}`, product.productLink || domainConfig?.shopLink)}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
